import * as THREE from 'three';
import { CelestialBody } from '@/universe/CelestialBody';
import {
  GLSL_CLOUD_FIELD,
  GLSL_CONSTANTS,
  GLSL_DITHER,
  GLSL_LIGHTING,
  GLSL_NOISE,
  glslFloat,
} from './common.glsl';
import { MIN_SCALE_HEIGHT, SUN_COLOR, SUN_INTENSITY_DEFAULT } from './atmosphereDefaults';
import {
  GLSL_SHADOW_CASCADE,
  GLSL_SHADOW_CASCADE_UNIFORMS,
  ShadowCascadeUniforms,
  createShadowCascadeUniforms,
} from './shadowCascade';

/**
 * Terminator ramp for ground illumination, in `dot(localUp, sunDirection)`.
 *
 * A real sunbeam does not stop at the geometric horizon and it is not at full
 * strength when it gets there: it reddens and thins out through a very long
 * grazing air path, so the ground darkens across a band around sunset rather
 * than at an instant. This ramp is that band.
 *
 * It is evaluated PER FRAGMENT, from the fragment's own local up. It used to
 * be evaluated once per frame from the camera's, and multiplied into
 * `uSunIntensity` before it reached the shader — which is indistinguishable
 * from the correct answer while standing on the ground (every visible fragment
 * shares the observer's local up to within a fraction of a degree) and plainly
 * wrong from anywhere else, because one number cannot describe the terminator
 * of a whole planet. Seen from orbit it dimmed and brightened *the entire
 * body's terrain at once* as the sub-camera point crossed its own terminator —
 * reported as the sun switching on and off.
 *
 * Exported because `Game` applies the same ramp, from the same camera-local
 * measure, to the things for which camera-local genuinely is right: the shared
 * `DirectionalLight` and the vegetation billboards, neither of which exists
 * far from the viewer. Sharing the numbers keeps the two terminators from
 * drifting apart.
 */
export const SURFACE_DAYLIGHT_START = -0.12;
export const SURFACE_DAYLIGHT_END = 0.12;

/**
 * Fraction of skylight reaching the ground once the sun is fully below the
 * ramp above. Not zero: the sky stays luminous well after sunset, it just
 * stops delivering much of that light downward.
 */
export const SURFACE_NIGHT_SKY_FRACTION = 0.08;

// TypeScript-side uniform defaults. The fragment shader's own tunables (noise
// frequencies, fade distances, material response, ...) are declared as GLSL
// consts inside the FRAGMENT template below instead of here — they are never
// read from TypeScript, so keeping them as plain GLSL keeps the value next to
// the shader code that actually uses it and avoids the int/float literal
// mismatches that come with interpolating numbers into a template string
// (see glslFloat in common.glsl.ts).
const DEFAULT_DETAIL_SCALE = 24;
const DEFAULT_DETAIL_STRENGTH = 0.45;
const DEFAULT_FOG_MULTIPLIER = 0.1;

/**
 * Procedural terrain material.
 *
 * There are no terrain textures. Surface appearance is built from:
 *
 *   - the per-vertex albedo the generator computed from elevation, biome and
 *     slope (this is what produces continental patterns visible from orbit),
 *   - triplanar procedural detail at three scales, faded in by camera distance
 *     so a surface that reads as a smooth continent from orbit resolves into
 *     sediment, cracks and pebbles on foot,
 *   - a normal perturbation derived from the same noise, so micro-relief
 *     catches the light instead of only tinting the albedo,
 *   - aerial perspective computed from the body's real atmosphere parameters,
 *     which is what actually sells distance at planetary scale.
 *
 * Detail noise is sampled in *planet-fixed* coordinates, reconstructed in the
 * vertex shader as `position + uChunkOrigin`. That matters: sampling in render
 * coordinates would make the detail swim across the ground every time the
 * floating origin shifts or the planet rotates.
 *
 * Chunks share one uniform object for everything global (sun, atmosphere,
 * quality) and carry their own `uChunkOrigin`. One shader program is compiled;
 * updating the shared uniforms updates every chunk at once.
 */

export interface SharedTerrainUniforms extends ShadowCascadeUniforms {
  uSunDirection: THREE.IUniform<THREE.Vector3>;
  uSunColor: THREE.IUniform<THREE.Color>;
  uSunIntensity: THREE.IUniform<number>;
  uAmbientSky: THREE.IUniform<THREE.Color>;
  uAmbientGround: THREE.IUniform<THREE.Color>;
  /** Direction toward the brightest neighbouring body. */
  uPlanetshineDir: THREE.IUniform<THREE.Vector3>;
  /** Neighbour albedo hue, unit-peak. */
  uPlanetshineColor: THREE.IUniform<THREE.Color>;
  /** Planetshine irradiance, in the same units as uSunIntensity. */
  uPlanetshine: THREE.IUniform<number>;
  /** Sun direction in PLANET-FIXED axes, for the cloud shadow lookup. */
  uSunDirectionFixed: THREE.IUniform<THREE.Vector3>;
  /** Cloud deck radius, metres. Zero disables cloud shadows entirely. */
  uCloudDeckRadius: THREE.IUniform<number>;
  uCloudCoverage: THREE.IUniform<number>;
  uCloudDrift: THREE.IUniform<number>;
  uCloudBanded: THREE.IUniform<number>;
  /** Simulation time, so the shadows drift with the deck that casts them. */
  uTime: THREE.IUniform<number>;
  uPlanetCentre: THREE.IUniform<THREE.Vector3>;
  uPlanetRadius: THREE.IUniform<number>;
  uScaleHeight: THREE.IUniform<number>;
  uRayleigh: THREE.IUniform<THREE.Vector3>;
  uMie: THREE.IUniform<number>;
  uHazeColor: THREE.IUniform<THREE.Color>;
  uSurfaceDensity: THREE.IUniform<number>;
  /** Multiplier on the terrain aerial-perspective optical depth. See its use below. */
  uFogMultiplier: THREE.IUniform<number>;
  uDetailScale: THREE.IUniform<number>;
  uDetailStrength: THREE.IUniform<number>;
  uCameraAltitude: THREE.IUniform<number>;
  uSeaLevelRadius: THREE.IUniform<number>;
  uNightEmissive: THREE.IUniform<THREE.Color>;
  uNightEmissiveStrength: THREE.IUniform<number>;
  /**
   * Eclipse occluder: another body's centre, in the same PLANET-FIXED axes as
   * `uSunDirectionFixed`, relative to this planet's own centre. Radius 0
   * disables the lookup, same convention as `uCloudDeckRadius`.
   */
  uEclipseCentre: THREE.IUniform<THREE.Vector3>;
  uEclipseRadius: THREE.IUniform<number>;
  /** Cross-fade against the impostor: 1 real terrain, 0 impostor. */
  uSurfaceFade: THREE.IUniform<number>;
}

// Exported (rather than kept module-private, as they used to be) only so the
// HMR accept handler at the bottom of this file can read the freshly
// re-executed values off the new module namespace — see the comment there.
export const VERTEX = /* glsl */ `
precision highp float;

attribute vec2 aSurface; // slope01, humidity

varying vec3 vColor;
varying vec3 vNormal;
varying vec3 vRenderPosition;
varying vec3 vPlanetPosition;
varying vec2 vSurface;

uniform vec3 uChunkOrigin;

void main() {
  vColor = color;
  vSurface = aSurface;

  vec4 worldPosition = modelMatrix * vec4(position, 1.0);
  vRenderPosition = worldPosition.xyz;
  // Planet-fixed coordinates: stable under floating-origin shifts and planetary
  // rotation, so procedural detail is locked to the ground.
  vPlanetPosition = position + uChunkOrigin;
  vNormal = normalize(mat3(modelMatrix) * normal);

  gl_Position = projectionMatrix * viewMatrix * worldPosition;
}
`;

export const FRAGMENT = /* glsl */ `
precision highp float;

${GLSL_CONSTANTS}
${GLSL_NOISE}
${GLSL_CLOUD_FIELD}
${GLSL_LIGHTING}

// Shader constants — tuned values for each visual subsystem, grouped in
// source order matching their first use in main() below. All magic numbers
// live here so they can be adjusted in one place without hunting through the
// shader body.
const float TOLERANCE = 1e-4;         // min denominator for normalisation guards
const float MIN_DETAIL_SCALE = 0.001; // guard against division-by-zero on uDetailScale
const float MIN_FOOTPRINT = 1e-6;     // guard against division-by-zero in fade calc

// Triplanar procedural detail: three noise octaves, each faded out both by
// screen-space footprint (so it never aliases) and by view distance (so far
// terrain skips the cost). See the "Procedural detail" block in main().
const float FINE_FREQ = 64.0;         // fine detail noise frequency (cracks/pebbles scale)
const float MID_FREQ = 6.0;           // mid band noise frequency
const float FAR_FREQ = 0.6;           // far band noise frequency
const float FOOTPRINT_MULT = 2.0;     // footprint-to-period safety ratio (Nyquist-ish margin)
const float NEAR_FADE_START = 60.0;   // near detail fade-in start, metres
const float NEAR_FADE_END = 900.0;    // near detail fade-out end, metres
const float MID_FADE_START = 600.0;   // mid detail fade-in start, metres
const float MID_FADE_END = 9000.0;    // mid detail fade-out end, metres
const float FAR_FADE_START = 9000.0;  // far detail fade-in start, metres
const float FAR_FADE_END = 140000.0;  // far detail fade-out end, metres
const float DETAIL_THRESHOLD = 0.002; // min fade weight before bothering to sample noise
const float FBM_BIAS = 0.5;           // fbm's neutral centre, used to blend bands as signed offsets
const float MID_BAND_WEIGHT = 0.7;    // mid band contribution to combined grain
const float FAR_BAND_WEIGHT = 0.5;    // far band contribution to combined grain

// Cracks/pebbles (near field only) and the normal perturbation derived from
// the same noise field, so micro-relief catches the light.
const float CRACK_THRESHOLD = 0.06;   // worley distance below which a texel counts as a crack
const float CRACK_STRENGTH = 0.35;    // crack albedo darkening
const float PERTURB_EPS = 0.004;      // finite-difference step for the perturbation gradient
const float PERTURB_FREQ = 48.0;      // perturbation noise frequency
const float PERTURB_REF_THRESH = 0.9; // |N.y| above which the tangent basis switches reference axis
const float PERTURB_STRENGTH_BASE = 1.4; // perturbation strength multiplier
const float PERTURB_SLOPE_WEIGHT = 0.4;  // perturbation's slope-dependent strength contribution

// A second, finer bump octave on top of the pebble-scale one above — sand/
// gravel-scale relief (dp/320 with the default uDetailScale of 24 is a ~7.5cm
// wavelength) that only reads as anything at all within a few paces of the
// camera, faded out well before the pebble layer above is. Same finite-
// difference technique, same 'no actual mesh' approach the coarser layer
// already uses, just one more scale of it.
const float PERTURB_FREQ_GRAIN = 320.0;      // fine grain perturbation frequency
const float VERY_NEAR_FADE_START = 3.0;      // grain fade-in start, metres
const float VERY_NEAR_FADE_END = 18.0;       // grain fade-out end, metres
const float PERTURB_STRENGTH_GRAIN_BASE = 0.55; // grain perturbation strength multiplier

// Wet/dry, rough/smooth material response.
const float WETNESS_MULT = 0.6;       // humidity * (1 - slope) -> wetness
const float WET_ALBEDO_DARKEN = 0.3;  // wetness albedo darkening factor
const float ROUGHNESS_MAX = 0.92;     // base roughness (dry, flat ground)
const float ROUGHNESS_WET_REDUCE = 0.55; // roughness reduction per unit wetness
const float ROUGHNESS_GRAIN_REDUCE = 0.1; // roughness reduction per unit grain
const float ROUGHNESS_MIN = 0.06;     // roughness floor

// Shadowing: cloud deck and eclipse occluder. The cascaded self-shadow — its
// uniforms, bias, PCF kernel and lookup — lives in shaders/shadowCascade.ts,
// shared verbatim with the ocean surface.
const float CLOUD_SHADOW_STRENGTH = 0.68; // cloud shadow max darkening (deck is thin, never opaque)
const float ECLIPSE_PENUMBRA_FRAC = 0.12; // penumbra width as a fraction of the occluder's radius

// Night-side emissive (settlement lights / lava glow): a sparse, clustered
// patch field rather than a wash across the whole unlit hemisphere.
const float NIGHT_FACTOR_SMOOTH_START = 0.05; // dot(up,L) where night emission starts ramping in
const float NIGHT_FACTOR_SMOOTH_END = -0.15;  // dot(up,L) where it reaches full strength
const float NIGHT_FACTOR_THRESH = 0.001;      // skip the whole block below this factor
const float NIGHT_DETAIL_SCALE = 900.0;       // night emission field scale multiplier (coarser than day detail)
const float NIGHT_GLOW_FREQ_MULT = 4.3;       // secondary octave's frequency multiplier
const float NIGHT_ALTITUDE_SCALE = 2500.0;    // altitude falloff scale for "low ground" weighting, metres
// Cluster/grain shaping (weights + thresholds) is shared with
// AstronomicalLayer's night glow — see NIGHT_GLOW_* in GLSL_CONSTANTS.
const float NIGHT_GRAIN_FREQ = 17.0;          // secondary grain field frequency
const float NIGHT_BASE_EMISSION = 0.25;       // emission floor, independent of altitude weighting
const float NIGHT_LOW_GROUND_WEIGHT = 0.75;   // additional emission weight biased toward low ground

// Aerial perspective (extinction + in-scatter of the air column between the
// fragment and the camera). The in-scatter tuning itself (floor, sun-up
// weight, twilight ramp) is shared with CloudLayer's own haze — see
// AERIAL_* in GLSL_CONSTANTS.
const float MEAN_ALTITUDE_FACTOR = 0.5;       // averages fragment and camera altitude for the density sample

// Ground terminator: see SURFACE_DAYLIGHT_START/END on the TypeScript side for
// what this ramp is and why it belongs here rather than in a uniform.
const float DAYLIGHT_START = ${glslFloat(SURFACE_DAYLIGHT_START)};
const float DAYLIGHT_END = ${glslFloat(SURFACE_DAYLIGHT_END)};
const float NIGHT_SKY_FRACTION = ${glslFloat(SURFACE_NIGHT_SKY_FRACTION)};

// Shallow-water tint below the sea-level datum.
const float WATER_DEPTH_SCALE = 400.0;   // depth at which the tint approaches full strength, metres
const float WATER_TINT_STRENGTH = 0.85;  // water tint blend strength
const vec3 WATER_TINT = vec3(0.18, 0.42, 0.55); // shallow water tint colour

varying vec3 vColor;
varying vec3 vNormal;
varying vec3 vRenderPosition;
varying vec3 vPlanetPosition;
varying vec2 vSurface;

uniform vec3 uSunDirection;
uniform vec3 uSunColor;
uniform float uSunIntensity;
uniform vec3 uAmbientSky;
uniform vec3 uAmbientGround;
uniform vec3 uPlanetshineDir;
uniform vec3 uPlanetshineColor;
uniform float uPlanetshine;
uniform vec3 uSunDirectionFixed;
uniform float uCloudDeckRadius;
uniform float uCloudCoverage;
uniform float uCloudDrift;
uniform float uCloudBanded;
uniform float uTime;
uniform vec3 uPlanetCentre;
uniform float uPlanetRadius;
uniform float uScaleHeight;
uniform vec3 uRayleigh;
uniform float uMie;
uniform vec3 uHazeColor;
uniform float uSurfaceDensity;
uniform float uFogMultiplier;
uniform float uDetailScale;
uniform float uDetailStrength;
uniform float uCameraAltitude;
uniform float uSeaLevelRadius;
uniform vec3 uNightEmissive;
uniform float uNightEmissiveStrength;
uniform vec3 uEclipseCentre;
uniform float uEclipseRadius;
/**
 * Cross-fade against this body's distant impostor. 1 is fully real terrain,
 * 0 is fully impostor. See GLSL_DITHER for why this is a stochastic discard
 * rather than transparency.
 */
uniform float uSurfaceFade;

${GLSL_DITHER}
${GLSL_SHADOW_CASCADE_UNIFORMS}
${GLSL_SHADOW_CASCADE}

void main() {
  vec3 toCamera = cameraPosition - vRenderPosition;
  float viewDistance = length(toCamera);
  vec3 V = toCamera / max(viewDistance, TOLERANCE);

  vec3 planetRelative = vRenderPosition - uPlanetCentre;
  float fragRadius = length(planetRelative);
  vec3 up = planetRelative / max(fragRadius, TOLERANCE);
  vec3 N = normalize(vNormal);

  float slope = clamp(vSurface.x, 0.0, 1.0);
  float humidity = clamp(vSurface.y, 0.0, 1.0);

  // --- Procedural detail ---------------------------------------------------
  // Three scales, each faded out once its features are too small for the pixels
  // drawing them.
  //
  // Distance alone is not enough. What matters is how much ground a single
  // pixel covers, and at a grazing angle — which is most of the ground, most of
  // the time — that is far larger than the distance suggests. Fading on
  // distance leaves high-frequency noise being point-sampled across metres of
  // terrain per pixel, which shimmers violently as the camera moves and reads
  // as flickering triangles.
  //
  // The footprint comes from screen-space derivatives of the planet-fixed
  // sample coordinate, so each octave is faded out exactly when its period
  // drops below what the pixel can resolve. This is the analytic equivalent of
  // a mip chain for a function that has no texture to mip.
  vec3 dp = vPlanetPosition / max(uDetailScale, MIN_DETAIL_SCALE);
  float footprint = max(length(dFdx(dp)), length(dFdy(dp)));

  // An octave at frequency f has period 1/f; it is safe while that period is at
  // least twice the footprint, and is faded out below that.
  float fineFade = clamp(1.0 / max(footprint * FINE_FREQ * FOOTPRINT_MULT, MIN_FOOTPRINT) - 1.0, 0.0, 1.0);
  float midBandFade = clamp(1.0 / max(footprint * MID_FREQ * FOOTPRINT_MULT, MIN_FOOTPRINT) - 1.0, 0.0, 1.0);
  float farBandFade = clamp(1.0 / max(footprint * FAR_FREQ * FOOTPRINT_MULT, MIN_FOOTPRINT) - 1.0, 0.0, 1.0);

  // Distance still gates the most expensive band so far terrain skips the work.
  float nearFade = (1.0 - smoothstep(NEAR_FADE_START, NEAR_FADE_END, viewDistance)) * fineFade;
  float midFade = (1.0 - smoothstep(MID_FADE_START, MID_FADE_END, viewDistance)) * midBandFade;
  float farFade = (1.0 - smoothstep(FAR_FADE_START, FAR_FADE_END, viewDistance)) * farBandFade;

  float detailFine = nearFade > DETAIL_THRESHOLD ? fbm(dp * FINE_FREQ, 3) : FBM_BIAS;
  float detailMid = midFade > DETAIL_THRESHOLD ? fbm(dp * MID_FREQ, 4) : FBM_BIAS;
  float detailFar = farFade > DETAIL_THRESHOLD ? fbm(dp * FAR_FREQ, 3) : FBM_BIAS;

  float grain = (detailFine - FBM_BIAS) * nearFade
               + (detailMid - FBM_BIAS) * midFade * MID_BAND_WEIGHT
               + (detailFar - FBM_BIAS) * farFade * FAR_BAND_WEIGHT;

  vec3 albedo = vColor * (1.0 + grain * uDetailStrength);

  // Cracks and pebbles: only meaningful up close, and only while the pixel
  // footprint can actually resolve them.
  if (nearFade > 0.01) {
    float cracks = smoothstep(CRACK_THRESHOLD, 0.0, worley(dp * 22.0));
    albedo *= 1.0 - cracks * CRACK_STRENGTH * nearFade;

    // Normal perturbation from the same field, so micro-relief catches light.
    float e = PERTURB_EPS;
    float h0 = fbm(dp * PERTURB_FREQ, 3);
    float hx = fbm(dp * PERTURB_FREQ + vec3(e, 0.0, 0.0), 3);
    float hz = fbm(dp * PERTURB_FREQ + vec3(0.0, 0.0, e), 3);
    vec3 ref = abs(N.y) < PERTURB_REF_THRESH ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    vec3 tangent = normalize(cross(ref, N));
    vec3 bitangent = cross(N, tangent);
    float strength = PERTURB_STRENGTH_BASE * nearFade * (PERTURB_SLOPE_WEIGHT + slope);
    N = normalize(N - tangent * (hx - h0) * strength - bitangent * (hz - h0) * strength);
  }

  // Fine grain, walking-distance only: same Nyquist guard as the albedo bands
  // above, at this layer's own much higher frequency, so it never point-
  // samples noise the pixel footprint can't resolve either.
  float grainFootprintFade = clamp(1.0 / max(footprint * PERTURB_FREQ_GRAIN * FOOTPRINT_MULT, MIN_FOOTPRINT) - 1.0, 0.0, 1.0);
  float veryNearFade = (1.0 - smoothstep(VERY_NEAR_FADE_START, VERY_NEAR_FADE_END, viewDistance)) * grainFootprintFade;
  if (veryNearFade > 0.01) {
    float eg = PERTURB_EPS;
    float g0 = fbm(dp * PERTURB_FREQ_GRAIN, 2);
    float gx = fbm(dp * PERTURB_FREQ_GRAIN + vec3(eg, 0.0, 0.0), 2);
    float gz = fbm(dp * PERTURB_FREQ_GRAIN + vec3(0.0, 0.0, eg), 2);
    vec3 refG = abs(N.y) < PERTURB_REF_THRESH ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    vec3 tangentG = normalize(cross(refG, N));
    vec3 bitangentG = cross(N, tangentG);
    float strengthG = PERTURB_STRENGTH_GRAIN_BASE * veryNearFade * (PERTURB_SLOPE_WEIGHT + slope);
    N = normalize(N - tangentG * (gx - g0) * strengthG - bitangentG * (gz - g0) * strengthG);
  }

  // --- Material response ---------------------------------------------------
  // Wet ground is darker and glossier; steep ground is bare rock.
  float wetness = humidity * (1.0 - slope) * WETNESS_MULT;
  albedo *= 1.0 - wetness * WET_ALBEDO_DARKEN;
  float roughness = clamp(ROUGHNESS_MAX - wetness * ROUGHNESS_WET_REDUCE - grain * ROUGHNESS_GRAIN_REDUCE, ROUGHNESS_MIN, 1.0);

  vec3 L = normalize(uSunDirection);

  // --- Cloud shadow --------------------------------------------------------
  // Where does the ray from this point to the star cross the cloud deck? Sample
  // the deck's own density field there. One lookup, no march: the deck is a
  // shell, so the ray crosses it exactly once going outward.
  //
  // The quadratic is written in the factored form deliberately. The naive
  // |P|^2 - r^2 has both terms near 3.7e13 at planetary radii, and catastrophic
  // cancellation there is what made the atmosphere invisible on a real GPU while
  // it looked perfect under SwiftShader. (|P| - r)(|P| + r) is the same value
  // computed without ever forming the difference of two huge numbers.
  float cloudShadow = 1.0;
  if (uCloudDeckRadius > 0.0 && uCloudCoverage > 0.0) {
    float pLen = length(vPlanetPosition);
    float b = dot(vPlanetPosition, uSunDirectionFixed);
    float c = (pLen - uCloudDeckRadius) * (pLen + uCloudDeckRadius);
    float disc = b * b - c;
    if (disc > 0.0) {
      float t = -b + sqrt(disc);
      if (t > 0.0) {
        vec3 hit = normalize(vPlanetPosition + uSunDirectionFixed * t);
        float shadowDetail;
        float d = cloudDensityAt(hit, uTime, uCloudDrift, uCloudCoverage, uCloudBanded, shadowDetail);
        // Not opaque. A deck this thin passes a good deal of light, and a shadow
        // that reaches zero reads as a hole cut in the world rather than cloud.
        cloudShadow = 1.0 - d * CLOUD_SHADOW_STRENGTH;
      }
    }
  }

  // --- Eclipse shadow --------------------------------------------------------
  // Another body's disc crossing the sun as seen from here. Same ray-sphere
  // technique as the cloud shadow above, generalised to a sphere that is not
  // centred on this planet: 'oc' is fragment-to-occluder-centre, and the
  // factored form is taken on ITS length rather than on 'vPlanetPosition'
  // directly, which keeps the same cancellation-free property at the same
  // planetary scale.
  float eclipseShadow = 1.0;
  if (uEclipseRadius > 0.0) {
    vec3 oc = vPlanetPosition - uEclipseCentre;
    float ocLen = length(oc);
    float b = dot(oc, uSunDirectionFixed);
    float c = (ocLen - uEclipseRadius) * (ocLen + uEclipseRadius);
    float disc = b * b - c;
    if (disc > 0.0) {
      float t = -b - sqrt(disc);
      if (t > 0.0) {
        // Soft penumbra: how deep past the sphere's silhouette the ray
        // passes, relative to the occluder's own size. A hard cutoff reads
        // as a coloured disc painted on the ground; real eclipses have a
        // soft edge because the star is not a point.
        float closest = length(oc - uSunDirectionFixed * b);
        float penumbra = uEclipseRadius * ECLIPSE_PENUMBRA_FRAC;
        eclipseShadow = smoothstep(uEclipseRadius - penumbra, uEclipseRadius + penumbra, closest);
      }
    }
  }

  // --- Terrain self-shadow (cascaded) ---------------------------------------
  float selfShadow = terrainSelfShadow(vRenderPosition, N, L);

  // Shadow the direct term only. Ambient is skylight arriving from the whole
  // hemisphere, most of which is not behind this particular cloud (or eclipse,
  // or ridge), and attenuating it too is what makes shadowed ground look dead
  // instead of blue.
  float directShadow = cloudShadow * eclipseShadow * selfShadow;

  // This fragment's own place in the day. 'N' is the local surface normal and
  // already handles which way a slope faces; this is the separate question of
  // whether the beam reaching that slope has been attenuated by its own
  // grazing path through the air, which depends on the local vertical and not
  // on the normal. Both are needed: a slope tilted toward a setting sun keeps
  // a high 'dot(N, L)' long after the beam lighting it has thinned out.
  float fragDaylight = smoothstep(DAYLIGHT_START, DAYLIGHT_END, dot(up, L));

  vec3 color = shadeSurface(
    albedo, N, V, L, uSunColor, uSunIntensity * directShadow * fragDaylight, roughness, 0.0,
    // Skylight reaching the ground is gated by the same ramp, on the same
    // fragment. The sky itself is not dimmed by this — only how much of it
    // arrives down here — so the horizon can still burn after sunset over
    // ground that has gone dark.
    uAmbientSky * mix(NIGHT_SKY_FRACTION, 1.0, fragDaylight),
    uAmbientGround, up);

  // Planetshine. Wrapped rather than clamped Lambert: a neighbour large enough
  // to matter subtends a wide solid angle, so its terminator on this surface is
  // very soft and a hard max(dot(N,L),0) would draw an edge that is not there.
  // This is the only fill light that does not vanish with atmospheric density,
  // which is what keeps a sunless slope on an airless world from going black.
  if (uPlanetshine > 0.0) {
    float wrapped = clamp(dot(N, uPlanetshineDir) * 0.5 + 0.5, 0.0, 1.0);
    color += albedo * uPlanetshineColor * uPlanetshine * wrapped / PI;
  }

  // Night-side emissive: settlement lights, lava glow.
  //
  // Applied as a *patch* rather than a wash. A constant term across the unlit
  // hemisphere paints the entire night side in the emissive colour, which on a
  // volcanic world is a solid orange half-disc with a hard edge down the
  // terminator — a coloured shape, not a planet at night. Real night-side
  // emission covers a small fraction of the surface: lava is in the fissures,
  // lights are in the settlements. Sampling the same planet-fixed coordinate
  // the surface detail uses keeps the glow locked to the ground as the planet
  // turns, and biases it into the low ground where both actually collect.
  float nightFactor = smoothstep(NIGHT_FACTOR_SMOOTH_START, NIGHT_FACTOR_SMOOTH_END, dot(up, L));
  if (nightFactor > NIGHT_FACTOR_THRESH && uNightEmissiveStrength > 0.0) {
    vec3 np = vPlanetPosition / max(uDetailScale * NIGHT_DETAIL_SCALE, 1.0);
    float glowField = fbm(np, 4) * NIGHT_GLOW_PRIMARY_WEIGHT + fbm(np * NIGHT_GLOW_FREQ_MULT, 3) * NIGHT_GLOW_SECONDARY_WEIGHT;
    float lowGround = 1.0 - clamp((fragRadius - uPlanetRadius) / NIGHT_ALTITUDE_SCALE, 0.0, 1.0);

    // Settlements are small, sparse and clustered. smoothstep(0.52, 0.80) over
    // a smooth fbm is none of those: it passes roughly a third of the surface
    // through a soft ramp, so the result reads as whole regions of ground
    // quietly luminous rather than as lights on it — and the night exposure
    // adaptation made that far more obvious than it used to be.
    //
    // Threshold raised and narrowed so only the top few percent of the field
    // lights at all, then broken up by a second, much finer field so what
    // survives is a scatter of discrete patches instead of one smooth blob. The
    // cube sharpens the falloff further, giving each patch a bright centre that
    // fades quickly, which is what a town looks like from above.
    float cluster = smoothstep(NIGHT_GLOW_CLUSTER_START, NIGHT_GLOW_CLUSTER_END, glowField);
    float grain = smoothstep(NIGHT_GLOW_GRAIN_START, NIGHT_GLOW_GRAIN_END, fbm(np * NIGHT_GRAIN_FREQ, 3));
    float emission = cluster * cluster * cluster * grain * (NIGHT_BASE_EMISSION + lowGround * NIGHT_LOW_GROUND_WEIGHT);
    color += uNightEmissive * uNightEmissiveStrength * nightFactor * emission;
  }

  // --- Aerial perspective --------------------------------------------------
  // Optical depth of the air column between the fragment and the camera,
  // approximated with the mean density along the segment.
  //
  // uFogMultiplier is a fudge on top of the physical density: viewDistance at
  // altitude includes long grazing rays toward the horizon, and taking those
  // at face value bleached the ground into the haze colour well before the
  // horizon itself, leaving no terrain detail visible from a descent. It was
  // an unnamed *1.6 on the extinction term alone before this was pulled out;
  // folding it into opticalDepth instead makes it scale extinction and
  // in-scatter consistently, and gives it a value that can be tuned without
  // touching the rest of this block. 0.2 is fit to keep near-field texture
  // legible from a several-kilometre descent while still letting distant
  // ground haze out.
  if (uSurfaceDensity > 0.0) {
    float fragAltitude = fragRadius - uPlanetRadius;
    float meanAltitude = max(0.0, (fragAltitude + uCameraAltitude) * MEAN_ALTITUDE_FACTOR);
    float density = uSurfaceDensity * exp(-meanAltitude / max(uScaleHeight, AERIAL_SCALE_HEIGHT_FLOOR));
    float opticalDepth = viewDistance * density * uFogMultiplier;

    vec3 extinction = exp(-(uRayleigh + vec3(uMie)) * opticalDepth);
    float sunElev = dot(up, L);
    float sunUp = clamp(sunElev * 0.5 + 0.5, 0.0, 1.0);

    // In-scattered light needs sunlit air along the view ray, so it has to
    // vanish on the night side. The AERIAL_MULTI_SCATTER_FLOOR below (see
    // GLSL_CONSTANTS) models the multiple scattering that keeps a hazy sky
    // luminous away from the sun, but the floor alone has no notion of local
    // time, and uSunIntensity is set from the body's distance to the star
    // rather than from the hour — it is still 3.2 at Halcyon's midnight.
    // Unattenuated, that left every distant ridge in-scattering 0.32-0.52
    // radiance in the dark: brighter than a daylit 0.3-albedo surface, and
    // brightest where the air column was longest, so the horizon glowed blue
    // and the foreground stayed black.
    //
    // The ramp is deliberately wider than the nightFactor band above, and
    // centred on the same point: airglow and haze should fade out across
    // twilight well after the settlement lights have come up, not snap off
    // with them at the terminator.
    float sunlitAir = smoothstep(AERIAL_SUNLIT_AIR_START, AERIAL_SUNLIT_AIR_END, sunElev);
    vec3 inscatter =
      uHazeColor * uSunColor * uSunIntensity * (1.0 - extinction)
      * (AERIAL_MULTI_SCATTER_FLOOR + sunUp * AERIAL_INSCATTER_SUNUP_WEIGHT) * sunlitAir;

    // Low sun: the beam illuminating this air column is reddened and dimmed by
    // its own long path through the atmosphere (the ray-marched shell integrates
    // this exactly; here it is the closed-form plane-parallel airmass, floored
    // to stay finite at the horizon). The optical depth of that beam is the
    // vertical column (surface density times scale height) times the airmass,
    // expressed relative to the zenith so daytime fog keeps its normal colour
    // and only the sunset/sunrise shift appears. Rayleigh attenuates the blue
    // end of the beam first, which is what turns the haze warm. The surface
    // density is clamped the way the shell clamps uDensityScale, so outlier
    // worlds can't drive the whole fog to black.
    float beamDensityScale = clamp(uSurfaceDensity / AERIAL_DENSITY_SCALE_NOMINAL,
                                   AERIAL_DENSITY_SCALE_MIN, AERIAL_DENSITY_SCALE_MAX);
    float sunAirmass = 1.0 / max(sunElev, AERIAL_SUN_BEAM_SIN_FLOOR);
    vec3 beamTau = (uRayleigh + vec3(uMie) * AERIAL_MIE_OPTICAL_DEPTH_GAIN)
                 * beamDensityScale * uScaleHeight * (sunAirmass - 1.0);
    inscatter *= exp(-beamTau);

    color = color * extinction + inscatter;
  }

  // Shallow-water tint for terrain below the waterline. The alpha channel is
  // also an explicit dry-land mask for the ocean's opaque SSR capture: opaque
  // world rendering ignores material alpha, while the off-screen RGBA target
  // retains it. This lets coastal foam follow the actual waterline instead of
  // trying to infer it from depth-displaced terrain LOD chunks.
  float dryLandMask = 1.0;
  if (uSeaLevelRadius > 0.0) {
    float signedHeightAboveSea = fragRadius - uSeaLevelRadius;
    dryLandMask = smoothstep(-0.12, 0.12, signedHeightAboveSea);
    float depthBelowSea = -signedHeightAboveSea;
    if (depthBelowSea > 0.0) {
      float t = clamp(depthBelowSea / WATER_DEPTH_SCALE, 0.0, 1.0);
      color = mix(color, color * WATER_TINT, t * WATER_TINT_STRENGTH);
    }
  }

  // Deliberately the last thing in the shader. An early-out placed ahead of
  // the cascade PCF loop above is exactly the pattern that makes an ANGLE/D3D11
  // cross-compile synthesize a "hasn't exited yet" guard around a
  // texture-sampling loop and warn X3595 — see the note on terrainSelfShadow.
  // Discarding here costs a fully shaded fragment, which is the right trade for
  // something that runs for half a second at a promotion boundary.
  if (ditheredOut(uSurfaceFade, gl_FragCoord.xy)) discard;

  gl_FragColor = vec4(color, dryLandMask);

  // Everything above this line is radiance in linear space. These two chunks
  // are what carry it to the display, and omitting them does not merely skip a
  // filmic curve — it writes linear radiance into an sRGB framebuffer.
  //
  // Three applies tone mapping and output encoding by INVOKING these chunks in
  // the built-in materials' fragment shaders. It does not wrap a ShaderMaterial
  // for you. The functions and the toneMappingExposure uniform are compiled
  // into the program regardless, so a custom shader that never calls them is
  // silently exempt from both, while the scene background - which goes through
  // the renderer's own path - is not.
  //
  // The result was a pipeline in two different colour spaces at once. Terrain
  // was written linear and read back exactly linear: a 0.5-albedo surface at
  // Kolyma's noon computes 0.133 radiance and landed on screen at 34/255, which
  // is 0.133. Correctly encoded it is 0.40. That is the whole of "Kolyma reads
  // almost black", and it is why terrain brightness did not move at all across
  // an eight-fold sweep of the exposure.
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/** Builds the shared uniform block for one body. */
export function createSharedTerrainUniforms(body: CelestialBody, seaLevelRadius: number): SharedTerrainUniforms {
  const atm = body.atmosphere;
  const palette = body.def.palette;
  return {
    uSunDirection: { value: new THREE.Vector3(1, 0, 0) },
    uSunColor: { value: new THREE.Color(...SUN_COLOR) },
    uSunIntensity: { value: SUN_INTENSITY_DEFAULT },
    uAmbientSky: { value: new THREE.Color(0.02, 0.03, 0.05) },
    uAmbientGround: { value: new THREE.Color(0.008, 0.008, 0.009) },
    uPlanetshineDir: { value: new THREE.Vector3(0, 1, 0) },
    uPlanetshineColor: { value: new THREE.Color(1, 1, 1) },
    uPlanetshine: { value: 0 },
    uSunDirectionFixed: { value: new THREE.Vector3(1, 0, 0) },
    uCloudDeckRadius: { value: 0 },
    uCloudCoverage: { value: 0 },
    uCloudDrift: { value: 0 },
    uCloudBanded: { value: 0 },
    uTime: { value: 0 },
    uPlanetCentre: { value: new THREE.Vector3() },
    uPlanetRadius: { value: body.radius },
    uScaleHeight: { value: Math.max(MIN_SCALE_HEIGHT, atm.scaleHeight) },
    uRayleigh: { value: new THREE.Vector3(atm.rayleigh[0], atm.rayleigh[1], atm.rayleigh[2]) },
    uMie: { value: atm.mie },
    uHazeColor: { value: new THREE.Color(atm.hazeColor[0], atm.hazeColor[1], atm.hazeColor[2]) },
    uSurfaceDensity: { value: atm.surfaceDensity },
    uFogMultiplier: { value: DEFAULT_FOG_MULTIPLIER },
    uDetailScale: { value: DEFAULT_DETAIL_SCALE },
    uDetailStrength: { value: DEFAULT_DETAIL_STRENGTH },
    uCameraAltitude: { value: 0 },
    uSeaLevelRadius: { value: Number.isFinite(seaLevelRadius) ? seaLevelRadius : -1 },
    uNightEmissive: {
      value: new THREE.Color(palette.nightEmissive[0], palette.nightEmissive[1], palette.nightEmissive[2]),
    },
    uNightEmissiveStrength: { value: palette.nightEmissiveStrength },
    uEclipseCentre: { value: new THREE.Vector3() },
    uEclipseRadius: { value: 0 },
    uSurfaceFade: { value: 1 },
    ...createShadowCascadeUniforms(),
  };
}

/**
 * Live chunk materials, tracked only so a hot-reloaded shader can be re-linked
 * into every one of them in place. Kept in `import.meta.hot.data` rather than
 * a plain module-level `Set`: Vite re-executes this module's top level on
 * every hot update, so a plain `const` would silently reset to empty on the
 * very first edit and the accept handler below would have nothing to patch.
 * `hot.data` is Vite's own mechanism for carrying state across a module's own
 * replacement. `undefined` outside dev (production build, or under vitest),
 * so every use of this is gated on `import.meta.hot` being truthy.
 */
const liveTerrainMaterials: Set<THREE.ShaderMaterial> =
  import.meta.hot?.data.liveTerrainMaterials ?? new Set();
if (import.meta.hot) import.meta.hot.data.liveTerrainMaterials = liveTerrainMaterials;

/**
 * Creates a material for one chunk. The shared uniform *objects* are reused by
 * reference, so a single write updates every chunk; only `uChunkOrigin` is
 * per-material.
 */
export function createChunkMaterial(
  shared: SharedTerrainUniforms,
  chunkOrigin: THREE.Vector3,
  wireframe = false,
): THREE.ShaderMaterial {
  const material = new THREE.ShaderMaterial({
    uniforms: {
      ...shared,
      uChunkOrigin: { value: chunkOrigin },
    },
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    vertexColors: true,
    side: THREE.FrontSide,
    transparent: false,
    depthWrite: true,
    depthTest: true,
    wireframe,
  });
  if (import.meta.hot) {
    liveTerrainMaterials.add(material);
    // `dispose()` is already called by TerrainRenderer.removeChunk / dispose
    // whenever a chunk unloads, so listening for it is enough to keep this
    // set from accumulating materials for chunks that no longer exist.
    material.addEventListener('dispose', () => liveTerrainMaterials.delete(material));
  }
  return material;
}

/**
 * Hot-swaps every live chunk's shader source in place instead of reloading the
 * page. Ship position, camera orientation and simulation time all survive,
 * which is the actual point: tuning lighting or noise by eye currently means
 * flying back to the same spot after every edit.
 *
 * Two things this does NOT cover, both needing a full reload same as before:
 *
 *   - A uniform added or removed here. Existing materials keep the uniform
 *     set they were built with — `createChunkMaterial` is not re-run for
 *     already-live chunks — so a shader referencing a uniform nothing ever
 *     supplied a value for will not error, it will just read as zero.
 *   - Editing `common.glsl.ts`. It is shared with the cloud, atmosphere and
 *     sky shaders, none of which accept their own updates yet, so a change to
 *     it still walks up to a full reload. Only edits to this file's own
 *     `VERTEX`/`FRAGMENT` are hot-swapped.
 */
if (import.meta.hot) {
  import.meta.hot.accept((mod) => {
    if (!mod) return;
    for (const material of liveTerrainMaterials) {
      material.vertexShader = mod.VERTEX;
      material.fragmentShader = mod.FRAGMENT;
      material.needsUpdate = true;
    }
    console.log(`[hmr] re-linked ${liveTerrainMaterials.size} terrain chunk material(s)`);
  });
}
